/**
 * @file actions/Newsletters/actions.js
 * @copyright 2019 CBC/Radio-Canada. All rights reserved.
 * @author Abdel Bolanos [abdel.bolanos.martinez@cbc.ca]
 */

import * as CONSTANTS from "./constants";
import { request, requestJSON } from "../../utils/promises/library";
import log from "../../utils/logger/library";
import {
    load_newsletters_data,
    newsletters_subscribe_email,
    newsletters_middleware_error,
} from "../../reducers/Newsletters/reducer";
import { parseNewsletterIdsFromPageSections } from "../../components/NewsletterWidget/utils";
import { isBrowser } from "../../utils/environmentDetection/library";

/**
 * Fetches newsletters data for the given newsletter IDs.
 *
 * @param {...string} newsletter_ids - The IDs of the newsletters to fetch.
 * @returns {Function} - An async thunk that dispatches the fetched newsletters data.
 */
export const fetch_some_newsletters = (...newsletter_ids) => {
    return async (dispatch) => {
        try {
            // If we're client-side, we fetch the entire newsletters state on the first newsletter load
            // therefore there's no need to fetch single newsletters
            if (isBrowser()) return;
            if (newsletter_ids.length === 0) return;

            const newsletterPromises = newsletter_ids.map(
                async (newsletter_id) => {
                    const apiData = await requestJSON(
                        `${CONSTANTS.SINGLE_NEWSLETTER_ENDPOINT}/${newsletter_id}`
                    );
                    return apiData?.data || null;
                }
            );
            const newsletters = (await Promise.all(newsletterPromises)).filter(
                Boolean
            );
            dispatch(load_newsletters_data({ newsletters }));
        } catch (error) {
            log.error(`(fetch_some_newsletters) request failed`, error);
        }
    };
};

/**
 * Fetches newsletters if needed and if there's any available on the page sections.
 *
 * @param {Array} pageSections - The page sections to parse newsletter IDs from.
 * @returns {Function} - async thunk that fetches the newsletters and updates the store
 */
export const fetch_newsletters_if_needed = (pageSections) => {
    return async (dispatch, getState) => {
        const state = getState();
        const newsletters = state?.newsletters?.newsletters || null;

        if (!newsletters?.length || newsletters === "false") {
            const parsedNewsletters =
                parseNewsletterIdsFromPageSections(pageSections);
            await dispatch(fetch_some_newsletters(...parsedNewsletters));
        }
    };
};

/**
 * Fetches all newsletter state.
 * @returns {Function} The async function that dispatches the action.
 */
export const fetch_all_newsletter_state = () => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const newsletterState = state?.newsletters?.newsletters || null;

            if (
                newsletterState === "false" ||
                !newsletterState?.length ||
                newsletterState?.length === 1
            ) {
                const apiData = await requestJSON(
                    CONSTANTS.NEWSLETTERS_ENDPOINT
                );
                const newsletters = apiData?.data || false;
                dispatch(load_newsletters_data({ newsletters }));
            }
        } catch (error) {
            log.error(`(fetch_all_newsletter_state) request failed`, error);
        }
    };
};

/**
 * Subscribes the user to the newsletter after verifying recaptcha / NONCaptcha
 * @param {string} newsletter_id  - The newsletter id
 * @param {string} newsletter_name  - The newsletter name
 * @param {string} email - Email of the subscriber
 * @param {string} current_time  - Curernt time (Used for NONCAPTCHA)
 * @param {string} business_number - unique id Used for noncaptcha
 * @param {string} recaptcha_token  - if recaptcha is enabled we will pass in a recaptcha token
 * @param {string} action - the user action
 * @returns
 */
export const post_subscribe_newsletters_email = (
    newsletter_id,
    newsletter_name,
    email,
    current_time,
    business_number,
    recaptcha_token,
    action
) => {
    const NONCAPTCHA_THRESHOLD_MSECONDS = 2000;
    const now = new Date();
    const submit_time = now.getTime() - current_time;
    if (
        (submit_time < NONCAPTCHA_THRESHOLD_MSECONDS ||
            business_number !== "") &&
        !(
            typeof process !== "undefined" &&
            process?.env?.JEST_WORKER_ID !== undefined
        )
    ) {
        const request_data = {
            newsletter_id: newsletter_id,
            t_i: new Date(parseInt(current_time)),
            t_p: now,
        };

        let error_type = "";
        if (submit_time < NONCAPTCHA_THRESHOLD_MSECONDS) {
            error_type = "NonCaptcha time filter";
        }
        if (business_number !== "") {
            error_type = "NonCaptcha honeypot filter";
        }

        const middleware_error = {
            source: window.location.href,
            api_route: "n/a",
            request_data: request_data,
            middleware_type: "NonCaptcha",
            error_type: error_type,
        };
        return (dispatch) => {
            post_middleware_newsletters_api(
                dispatch,
                newsletter_id,
                middleware_error
            );

            new Error("This submission will fail silently.");
        };
    }
    return (dispatch) => {
        post_subscribe_newsletters_api(
            dispatch,
            newsletter_id,
            newsletter_name,
            email,
            recaptcha_token,
            action
        );
    };
};

const post_subscribe_newsletters_api = (
    dispatch,
    newsletter_id,
    newsletter_name,
    email,
    recaptcha_token,
    action
) => {
    return new Promise((resolve, reject) => {
        request(CONSTANTS.SUBSCRIBE_ENDPOINT, {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: `{"newsletter_id":"${newsletter_id}","email":"${email}","recaptcha_token":"${recaptcha_token}","action":"${action}"}`,
        })
            .then((response) => {
                if (response.ok) {
                    log.debug(`post_subscribe_newsletters call`);
                    return response.json();
                } else if (response.status === 500) {
                    return response.json();
                }
                throw new Error(response.statusText);
            })
            .catch((error) => {
                log.error(
                    `(post_subscribe_newsletters_api) request failed`,
                    error
                );
                dispatch(
                    newsletters_subscribe_email({
                        subscribe: {
                            errors: {
                                error: error,
                            },
                        },
                    })
                );
                reject(error);
            })
            .then((response) => {
                if (response && response.hasOwnProperty("error")) {
                    response = {
                        error: {
                            newsletter_id,
                        },
                        errors: response,
                    };
                }
                dispatch(newsletters_subscribe_email({ subscribe: response }));
                if (
                    response.message &&
                    response.message.result &&
                    !response.message.result.confirmation
                ) {
                    let adestra_id = null;
                    if (response.message.result.data) {
                        adestra_id = response.message.result.data.id;
                    }
                    window.CBC.APP.SC.EventTracker.trackEvent("SUBSCRIBED", {
                        content: {
                            title: newsletter_name,
                            id: newsletter_id,
                            type: "newsletter",
                        },
                        user: {
                            subscriptions: {
                                newsletters: [newsletter_id],
                            },
                            id: {
                                adestra: adestra_id,
                            },
                        },
                    });
                }

                resolve();
            });
    });
};

const post_middleware_newsletters_api = (
    dispatch,
    newsletter_id,
    middleware_error
) => {
    return new Promise((resolve, reject) => {
        request(CONSTANTS.MIDDLEWARE_ENDPOINT, {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(middleware_error),
        })
            .then((response) => {
                if (response.ok) {
                    log.debug(`post_middleware_newsletters call`);
                    return response.json();
                } else if (response.status === 500) {
                    return response.json();
                }
                throw new Error(response.statusText);
            })
            .catch((error) => {
                log.error(
                    `(post_middleware_newsletters_api) request failed`,
                    error
                );
                dispatch(
                    newsletters_middleware_error({
                        middleware_error: {
                            errors: { error: error },
                        },
                    })
                );
                reject(error);
            })
            .then((response) => {
                if (response && response.hasOwnProperty("error")) {
                    response = {
                        error: { newsletter_id },
                        errors: response,
                    };
                }
                dispatch(
                    newsletters_middleware_error({
                        middleware_error: response,
                    })
                );

                resolve();
            });
    });
};
